import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  inject,
  OnDestroy,
} from '@angular/core';
import { UnleashService } from '@karelics/angular-unleash-proxy-client';
import { STAR_NOTICE_BANNER_UNLEASH_KEY } from '../../tokens/unleash-flags.token';

const BANNER_HEIGHT_CSS_VAR = '--star-notice-banner-height';

@Component({
  selector: 'star-core-notice-banner',
  templateUrl: './notice-banner.component.html',
  styleUrls: ['./notice-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StarNoticeBannerComponent implements AfterViewInit, OnDestroy {
  @HostBinding('class.star-core-notice-banner') componentClass = true;

  // The same flag also gates the notice modal (`StarNoticeModalService`), so
  // both parts of the notice appear and disappear together.
  readonly isEnabled: boolean = inject(UnleashService).isEnabled(
    STAR_NOTICE_BANNER_UNLEASH_KEY,
  );

  private readonly document = inject(DOCUMENT);
  private readonly elRef: ElementRef<HTMLElement> = inject(ElementRef);
  private resizeObserver?: ResizeObserver;

  ngAfterViewInit(): void {
    if (!this.isEnabled) {
      return;
    }

    // Both header navs are `position: fixed`, so they have to be offset by
    // however tall this banner renders. Publishing the height as a CSS variable
    // keeps that offset correct for whatever copy the banner ends up carrying,
    // including when it wraps onto more lines on narrow viewports.
    this.publishHeight();

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.publishHeight());
      this.resizeObserver.observe(this.elRef.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.document.documentElement.style.removeProperty(BANNER_HEIGHT_CSS_VAR);
  }

  private publishHeight(): void {
    const { height } = this.elRef.nativeElement.getBoundingClientRect();

    this.document.documentElement.style.setProperty(
      BANNER_HEIGHT_CSS_VAR,
      `${height}px`,
    );
  }
}
